import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/WorkDetails.css";

import noWorkImage from "../assets/images/no_work.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function WorkDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workNotFound, setWorkNotFound] = useState(false);
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const [showDescriptionFull, setShowDescriptionFull] = useState(false);
  const [loadingDocumentId, setLoadingDocumentId] = useState(null);

  // Modals
  const [showApproveQuoteModal, setShowApproveQuoteModal] = useState(false);
  const [showRejectQuoteModal, setShowRejectQuoteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [approveDeclarationChecked, setApproveDeclarationChecked] = useState(false);
  const [rejectDeclarationChecked, setRejectDeclarationChecked] = useState(false);

  // Review modal state
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [loadingReview, setLoadingReview] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [contractorForReview, setContractorForReview] = useState(null);

  // Actions
  const [markingCompleted, setMarkingCompleted] = useState(false);
  const [processingQuoteAction, setProcessingQuoteAction] = useState(false);

  // Map refs
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Translations
  const t = {
    en: {
      loading: "Loading",
      workDetails: "Work Details",
      basicDetails: "Basic Details",
      locationDetails: "Location Details",
      contractorDetails: "Contractor Details",
      quoteDetails: "Quote Details",
      mapView: "Map View",
      getDirections: "Get Directions",
      address: "Address",
      company: "Company",
      name: "Name",
      phone: "Phone",
      whatsapp: "Whatsapp",
      email: "Email",
      ratings: "Ratings",
      clickToKnowMore: "(Click to know more)",
      clickToKnowRatings: "(Click to know the ratings)",
      description: "Description",
      readMore: "Read More",
      readLess: "Read Less",
      documents: "Documents",
      subCategories: "Sub Categories",
      specialisations: "Specialisations",
      quoteAmount: "Quote Amount",
      rejectionNote: "Rejection Note",
      rejectionNotePlaceholder: "Add a rejection note so that the contractor can work on his/her mistake.",
      approveQuote: "Approve Quote",
      rejectQuote: "Reject Quote",
      reviewContractor: "Review Contractor",
      markWorkCompleted: "Mark Work As Completed",
      open: "Open",
      completed: "Completed",
      confirmComplete: "Confirm",
      completeWorkConfirm: "Are you sure you want to mark this work as completed? This action cannot be reversed.",
      workMarkedCompleted: "Work marked as completed",
      pending: "Pending",
      accepted: "Accepted",
      rejected: "Rejected",
      postDate: "Post Date",
      endDate: "End Date",
      workNotFound: "Work not found",
      error: "Error",
      cancel: "Cancel",
      submit: "Submit",
      edit: "Edit",
      save: "Save",
      close: "Close",
      loadingApprove: "Approving...",
      loadingReject: "Rejecting...",
      loadingComplete: "Marking as completed...",
      addReview: "Add Review",
      editReview: "Edit Review",
      yourRating: "Ratings",
      yourReview: "Review",
      reviewPlaceholder: "Write a detailed review about the contractor",
      importantInfo: "Important Info",
      importantInfoText: "Approving this quotation will automatically mark all other submitted quotes as rejected, and the work request will be marked as completed. This action cannot be reversed.",
      declaration: "Declaration",
      approveDeclarationText: "I hereby confirm that I have reviewed and approved this contractor's quotation to the best of my knowledge. I understand that once approved, the quotation cannot be withdrawn or altered without valid reason.",
      rejectDeclarationText: "I confirm that I am rejecting this quotation based on valid reasons. I understand that repeated or unjustified rejections may affect my account's credibility on this platform.",
      agreeDeclaration: "I have read and agree to the above declaration.",
      rejectionNoteRequired: "Rejection note is required",
      quoteApproved: "Quote approved successfully",
      quoteRejected: "Quote rejected successfully",
      reviewSubmitted: "Review submitted successfully",
      reviewUpdated: "Review updated successfully",
    },
    ar: {
      loading: "جاري التحميل",
      workDetails: "تفاصيل العمل",
      basicDetails: "التفاصيل الأساسية",
      locationDetails: "تفاصيل الموقع",
      contractorDetails: "تفاصيل المقاول",
      quoteDetails: "تفاصيل العرض",
      mapView: "عرض الخريطة",
      getDirections: "الحصول على الاتجاهات",
      address: "العنوان",
      company: "الشركة",
      name: "الاسم",
      phone: "الهاتف",
      whatsapp: "واتساب",
      email: "البريد الإلكتروني",
      ratings: "التقييمات",
      clickToKnowMore: "(انقر لمعرفة المزيد)",
      clickToKnowRatings: "(انقر لمعرفة التقييمات)",
      description: "الوصف",
      readMore: "اقرأ المزيد",
      readLess: "اقرأ أقل",
      documents: "المستندات",
      subCategories: "الفئات الفرعية",
      specialisations: "التخصصات",
      quoteAmount: "مبلغ العرض",
      rejectionNote: "ملاحظة الرفض",
      rejectionNotePlaceholder: "أضف ملاحظة رفض حتى يتمكن المقاول من العمل على خطئه/خطئها.",
      approveQuote: "قبول العرض",
      rejectQuote: "رفض العرض",
      reviewContractor: "مراجعة المقاول",
      markWorkCompleted: "تعيين العمل كمكتمل",
      open: "مفتوح",
      completed: "مكتمل",
      confirmComplete: "تأكيد",
      completeWorkConfirm: "هل أنت متأكد أنك تريد تعيين هذا العمل كمكتمل؟ لا يمكن عكس هذا الإجراء.",
      workMarkedCompleted: "تم تعيين العمل كمكتمل",
      pending: "قيد الانتظار",
      accepted: "مقبول",
      rejected: "مرفوض",
      postDate: "تاريخ النشر",
      endDate: "تاريخ الانتهاء",
      workNotFound: "العمل غير موجود",
      error: "خطأ",
      cancel: "إلغاء",
      submit: "إرسال",
      edit: "تعديل",
      save: "حفظ",
      close: "إغلاق",
      loadingApprove: "جاري القبول...",
      loadingReject: "جاري الرفض...",
      loadingComplete: "جاري التعيين كمكتمل...",
      addReview: "إضافة تقييم",
      editReview: "تعديل التقييم",
      yourRating: "التقييمات",
      yourReview: "المراجعة",
      reviewPlaceholder: "اكتب مراجعة مفصلة عن المقاول",
      importantInfo: "معلومات مهمة",
      importantInfoText: "سيؤدي قبول هذا العرض تلقائياً إلى رفض جميع العروض الأخرى المقدمة، وستُعلَّم طلب العمل كمكتمل. لا يمكن عكس هذا الإجراء.",
      declaration: "الإقرار",
      approveDeclarationText: "أؤكد بأنني راجعت ووافقت على عرض هذا المقاول إلى أفضل علمي. أفهم أنه بمجرد الموافقة، لا يمكن سحب أو تغيير العرض دون سبب وجيه.",
      rejectDeclarationText: "أؤكد أنني أرفض هذا العرض لأسباب وجيهة. أفهم أن الرفض المتكرر أو غير المبرر قد يؤثر على مصداقية حسابي على هذه المنصة.",
      agreeDeclaration: "لقد قرأت ووافقت على الإقرار أعلاه.",
      rejectionNoteRequired: "ملاحظة الرفض مطلوبة",
      quoteApproved: "تم قبول العرض بنجاح",
      quoteRejected: "تم رفض العرض بنجاح",
      jobMarkedCompleted: "تم تعيين الوظيفة كمكتملة",
      reviewSubmitted: "تم إرسال المراجعة بنجاح",
      reviewUpdated: "تم تحديث المراجعة بنجاح",
    },
  }[lang];

  // Fetch work details
  useEffect(() => {
    const fetchWorkDetails = async () => {
      if (!id) {
        setWorkNotFound(true);
        setLoading(false);
        return;
      }

      // Validate ID format
      const idPattern = /^[a-fA-F0-9]{24}$/;
      if (!idPattern.test(id)) {
        setWorkNotFound(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setWorkNotFound(false);

        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/work/`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );

        if (data && data.response_code === 200 && data.response) {
          setWork(data.response);
        } else {
          setWorkNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching work details:", error);
        if (error.code === 404) {
          setWorkNotFound(true);
        } else {
          showToast(error.message || t.workNotFound, "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWorkDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, USER_BASE_URL, VERSION]);

  // Reload work details
  const reloadWorkDetails = async () => {
    if (!id) return;
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/`,
        "POST",
        JSON.stringify({ id }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response) {
        setWork(data.response);
      }
    } catch (error) {
      console.error("Error reloading work details:", error);
    }
  };

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!work?.location_coordinates || work.location_coordinates.length !== 2) {
      return;
    }

    if (!window.google || !window.google.maps || !mapRef.current) {
      return;
    }

    // location_coordinates is [longitude, latitude] format
    const lng = parseFloat(work.location_coordinates[0]);
    const lat = parseFloat(work.location_coordinates[1]);

    if (isNaN(lat) || isNaN(lng)) {
      return;
    }

    const center = { lat, lng };

    // Clear existing marker if it exists
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    // Create or update map
    let map = mapInstanceRef.current;
    if (!map) {
      map = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });
      mapInstanceRef.current = map;
    } else {
      map.setCenter(center);
    }

    // Wait for map to be ready before adding marker
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      const marker = new window.google.maps.Marker({
        position: center,
        map: map,
        draggable: false,
        visible: true,
      });

      markerRef.current = marker;
    });
  }, [work]);

  // Load Google Maps script
  useEffect(() => {
    if (!work || !work.location_coordinates) return;

    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      initializeMap();
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [work, initializeMap]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year} ${displayHours}:${minutes} ${ampm}`;
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount);
  };

  // Get category name
  const getCategoryName = () => {
    if (!work) return "";
    if (work.category_id && typeof work.category_id === "object") {
      return work.category_id.name || "";
    }
    return "";
  };

  // Get category badge color
  const getCategoryBadgeColor = (categoryName) => {
    if (!categoryName) return null;
    const name = categoryName.toLowerCase();
    if (name.includes("construction")) {
      return { bg: "#47c0c9", text: "#fff" };
    } else if (name.includes("maintenance")) {
      return { bg: "#10b981", text: "#fff" };
    } else if (name.includes("material") || name.includes("supply")) {
      return { bg: "#6b7280", text: "#fff" };
    }
    return { bg: "#e5e7eb", text: "#374151" };
  };

  // Get address string
  const getAddressString = () => {
    if (!work) return "";
    const parts = [];
    if (work.address1) parts.push(work.address1);
    if (work.address2) parts.push(work.address2);
    if (work.city) parts.push(work.city);
    if (work.state) parts.push(work.state);
    if (work.pincode) parts.push(`- ${work.pincode}`);
    return parts.join(", ");
  };

  // Handle edit click
  const handleEditClick = () => {
    if (!work?._id) return;
    // Navigate to EditWork page with work ID in URL
    navigate(`/edit-work/${work._id}`);
  };

  // Handle mark as completed click - show confirmation modal
  const handleMarkAsCompleted = () => {
    setShowCompleteConfirmModal(true);
  };

  // Confirm and mark as completed
  const confirmMarkAsCompleted = async () => {
    if (!work?._id) return;

    setMarkingCompleted(true);
    try {
      const formData = new FormData();
      formData.append("status", "completed");

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/work/edit/${work._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(t.workMarkedCompleted, "success");
        setShowCompleteConfirmModal(false);
        reloadWorkDetails();
      } else {
        showToast(data?.message || t.error, "error");
      }
    } catch (error) {
      console.error("Error marking work as completed:", error);
      showToast(error.message || t.error, "error");
    } finally {
      setMarkingCompleted(false);
    }
  };

  // Cancel mark as completed
  const cancelMarkAsCompleted = () => {
    setShowCompleteConfirmModal(false);
  };

  // Handle approve quote click
  const handleApproveQuoteClick = (quote) => {
    setSelectedQuote(quote);
    setApproveDeclarationChecked(false);
    setShowApproveQuoteModal(true);
  };

  // Handle reject quote click
  const handleRejectQuoteClick = (quote) => {
    setSelectedQuote(quote);
    setRejectionNote("");
    setRejectDeclarationChecked(false);
    setShowRejectQuoteModal(true);
  };

  // Handle approve quote
  const handleApproveQuote = async () => {
    if (!selectedQuote?._id || !approveDeclarationChecked) return;

    setProcessingQuoteAction(true);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote-action`,
        "POST",
        JSON.stringify({
          id: selectedQuote._id,
          status: "approved",
        }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        showToast(t.quoteApproved, "success");
        setShowApproveQuoteModal(false);
        setSelectedQuote(null);
        reloadWorkDetails();
      } else {
        showToast(data?.message || t.error, "error");
      }
    } catch (error) {
      console.error("Error approving quote:", error);
      showToast(error.message || t.error, "error");
    } finally {
      setProcessingQuoteAction(false);
    }
  };

  // Handle reject quote
  const handleRejectQuote = async () => {
    if (!selectedQuote?._id || !rejectDeclarationChecked) return;

    const trimmedNote = rejectionNote.trim();
    if (!trimmedNote) {
      showToast(t.rejectionNoteRequired, "error");
      return;
    }

    if (trimmedNote.length > 500) {
      showToast("Rejection note must be less than 500 characters", "error");
      return;
    }

    setProcessingQuoteAction(true);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote-action`,
        "POST",
        JSON.stringify({
          id: selectedQuote._id,
          status: "rejected",
          rejection_note: trimmedNote,
        }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        showToast(t.quoteRejected, "success");
        setShowRejectQuoteModal(false);
        setSelectedQuote(null);
        setRejectionNote("");
        reloadWorkDetails();
      } else {
        showToast(data?.message || t.error, "error");
      }
    } catch (error) {
      console.error("Error rejecting quote:", error);
      showToast(error.message || t.error, "error");
    } finally {
      setProcessingQuoteAction(false);
    }
  };

  // Handle review contractor click
  const handleReviewContractorClick = async () => {
    if (!work?.approved_quote?.contractor_id?._id) return;

    const contractorId = work.approved_quote.contractor_id._id;
    setContractorForReview(work.approved_quote.contractor_id);
    setShowReviewModal(true);
    setLoadingReview(true);

    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/contractor/fetch-review/`,
        "POST",
        JSON.stringify({ id: contractorId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response) {
        setReviewStars(data.response.star || 0);
        setReviewText(data.response.review || "");
      } else {
        setReviewStars(0);
        setReviewText("");
      }
    } catch (error) {
      console.error("Error fetching review:", error);
      setReviewStars(0);
      setReviewText("");
    } finally {
      setLoadingReview(false);
    }
  };

  // Handle submit review
  const handleSubmitReview = async () => {
    if (!contractorForReview?._id) return;

    if (!reviewStars || reviewStars < 1 || reviewStars > 5) {
      showToast("Please select a rating between 1 and 5 stars", "error");
      return;
    }

    const trimmedReview = reviewText.trim();
    if (!trimmedReview) {
      showToast("Please write a review", "error");
      return;
    }

    if (trimmedReview.length > 1000) {
      showToast("Review must be less than 1000 characters", "error");
      return;
    }

    setSubmittingReview(true);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/contractor/add-update-review/`,
        "POST",
        JSON.stringify({
          contractor_id: contractorForReview._id,
          star: reviewStars,
          review: trimmedReview,
        }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        showToast(
          reviewText ? t.reviewUpdated : t.reviewSubmitted,
          "success"
        );
        setShowReviewModal(false);
        setReviewStars(0);
        setReviewText("");
        setContractorForReview(null);
      } else {
        showToast(data?.message || t.error, "error");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      showToast(error.message || t.error, "error");
    } finally {
      setSubmittingReview(false);
    }
  };

  // Handle view document
  const handleViewDocument = async (docId, workId, isQuoteDoc = false) => {
    if (!docId || !workId) return;

    setLoadingDocumentId(docId);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/get-document/${workId}`,
        "POST",
        JSON.stringify({ key: docId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response?.signed_url) {
        window.open(data.response.signed_url, "_blank");
      } else {
        showToast("Document not available", "error");
      }
    } catch (error) {
      console.error("Error viewing document:", error);
      showToast(error.message || "Failed to view document", "error");
    } finally {
      setLoadingDocumentId(null);
    }
  };

  // Handle view quote document
  const handleViewQuoteDocument = async (docId, workId) => {
    if (!docId || !workId) return;

    setLoadingDocumentId(docId);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/get-document/${workId}`,
        "POST",
        JSON.stringify({ key: docId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response?.signed_url) {
        window.open(data.response.signed_url, "_blank");
      } else {
        showToast("Document not available", "error");
      }
    } catch (error) {
      console.error("Error viewing quote document:", error);
      showToast(error.message || "Failed to view document", "error");
    } finally {
      setLoadingDocumentId(null);
    }
  };

  // Get quotes to display
  const getQuotesToDisplay = () => {
    if (!work) return [];

    // If work is completed and has approved quote, show only approved quote
    if (work.status === "completed" && work.approved_quote) {
      return [work.approved_quote];
    }

    // Otherwise show all quotes
    if (work.quotes_received && work.quotes_received.length > 0) {
      return work.quotes_received;
    }

    return [];
  };

  // Render star rating
  const renderStars = (rating) => {
    return (
      <div className="rating-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`bx ${
              star <= Math.floor(rating)
                ? "bxs-star"
                : star <= rating
                ? "bxs-star-half"
                : "bx-star"
            }`}
            style={{ color: "#f59e0b" }}
          ></i>
        ))}
      </div>
    );
  };

  const categoryName = getCategoryName();
  const categoryBadgeColor = getCategoryBadgeColor(categoryName);
  const addressString = getAddressString();
  const quotesToDisplay = getQuotesToDisplay();
  const isCompleted = work?.status === "completed";
  const hasApprovedQuote = work?.approved_quote !== null && work?.approved_quote !== undefined;

  if (loading) {
    return (
      <div className="work-details-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (workNotFound || !work) {
    return (
      <div className="work-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="work-details-page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i>
          </button>
          <h1 className="work-details-page-title">{t.workDetails}</h1>
          <div className="header-spacer"></div>
        </div>
        <div className="work-not-found">
          <img src={noWorkImage} alt="No work found" className="no-work-image" />
          <p className="no-work-message">{t.workNotFound}</p>
        </div>
        <BottomNavbar currentPage="home" lang={lang} />
      </div>
    );
  }

  return (
    <div className="work-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      {/* Page Header */}
      <div className="work-details-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="work-details-page-title">{t.workDetails}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="work-details-content">
        {/* Basic Details */}
        <div className="work-details-section">
          <div className="work-details-section-title-container">
            <h2 className="work-details-section-title">{t.basicDetails}</h2>
            <span
              className={`work-status-badge ${isCompleted ? "completed" : "open"}`}
              style={{
                backgroundColor: isCompleted ? "#0AC26F" : "#47c0c9",
                color: "#fff",
              }}
            >
              {isCompleted ? t.completed : t.open}
            </span>
          </div>
          <div className="work-details-card">
            <div className="work-details-card-header">
              <div className="work-details-title-container">
                <h3 className="work-category-title">{work.title || getCategoryName()}</h3>
                {work.title && categoryName && categoryBadgeColor && (
                  <span
                    className="work-details-category-badge"
                    style={{
                      backgroundColor: categoryBadgeColor.bg,
                      color: categoryBadgeColor.text,
                    }}
                  >
                    {categoryName}
                  </span>
                )}
              </div>
              {!isCompleted && (
                <button
                  className="work-favorite-btn edit-work-btn"
                  onClick={handleEditClick}
                  title={t.edit}
                >
                  <i className="bx bx-edit"></i>
                </button>
              )}
            </div>

            <div className="work-info-item">
              <i className="bx bx-map"></i>
              <span>{addressString}</span>
            </div>

            <div className="work-info-item">
              <i className="bx bx-calendar"></i>
              <span>
                <b>{t.postDate}:</b> {formatDate(work.created_at)}
                {work.quote_last_date && (
                  <>
                    {" "}&nbsp;&nbsp;
                    <b>{t.endDate}:</b> {formatDate(work.quote_last_date)}
                  </>
                )}
              </span>
            </div>

            {work.work_amount && (
              <div className="work-info-item">
                <i className="bx bx-money"></i>
                <span>
                  AED {formatAmount(work.work_amount.min || 0)} - AED{" "}
                  {formatAmount(work.work_amount.max || 0)}
                </span>
              </div>
            )}

            {work.description && (
              <div className="work-description-section">
                <h4 className="work-subsection-title">{t.description}</h4>
                <p className={`work-description-text ${showDescriptionFull ? 'expanded' : ''}`}>
                  {(() => {
                    if (work.description.length <= 150) {
                      return work.description;
                    }
                    if (showDescriptionFull) {
                      return work.description;
                    }
                    return `${work.description.substring(0, 150)}...`;
                  })()}
                  {work.description.length > 150 && (
                    <>
                      {" "}
                      <button
                        className="read-more-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDescriptionFull((prev) => !prev);
                        }}
                        type="button"
                      >
                        {showDescriptionFull ? t.readLess : t.readMore}
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {work.documents && work.documents.length > 0 && (
              <div className="work-documents-section">
                <h4 className="work-subsection-title">{t.documents}</h4>
                <div className="work-documents-list">
                  {work.documents.map((doc, index) => {
                    const isLoading = loadingDocumentId === doc.id;
                    return (
                      <div
                        key={doc.id || index}
                        className={`work-document-item ${isLoading ? "loading" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (doc.id && !isLoading) {
                            handleViewDocument(doc.id, work._id, false);
                          } else if (!doc.id) {
                            showToast("Document ID not available", "error");
                          }
                        }}
                      >
                        {isLoading ? (
                          <div className="document-loading-spinner">
                            <div className="spinner-small"></div>
                          </div>
                        ) : (
                          <div className="document-icon">PDF</div>
                        )}
                        <span className="document-name">
                          {doc.id?.split("/").pop()?.substring(0, 15) || "Document"}
                          ...
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {work.sub_categories && work.sub_categories.length > 0 && (
              <div className="work-subcategories-section">
                <h4 className="work-subsection-title">{t.subCategories}</h4>
                <div className="work-subcategories-list">
                  {work.sub_categories.map((subCat, index) => (
                    <span key={subCat._id || index} className="subcategory-chip">
                      {subCat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {work.specialisations && work.specialisations.length > 0 && (
              <div className="work-subcategories-section">
                <h4 className="work-subsection-title">{t.specialisations}</h4>
                <div className="work-subcategories-list">
                  {work.specialisations.map((spec, index) => (
                    <span key={spec._id || index} className="subcategory-chip">
                      {spec.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Details */}
        {work.location_coordinates && work.location_coordinates.length === 2 && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.locationDetails}</h2>
            <div className="work-details-card">
              <h4 className="work-subsection-title">{t.mapView}</h4>
              <div ref={mapRef} className="work-map-container"></div>
              {work.location_coordinates && work.location_coordinates.length === 2 && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${work.location_coordinates[1]},${work.location_coordinates[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="get-directions-link"
                >
                  <i className="bx bx-navigation"></i>
                  {t.getDirections}
                </a>
              )}
              <h4 className="work-subsection-title" style={{ marginTop: "16px" }}>
                {t.address}
              </h4>
              <p className="work-address-text">{addressString}</p>
            </div>
          </div>
        )}

        {/* Contractor Details - Only show if completed and has approved quote */}
        {isCompleted && hasApprovedQuote && work.approved_quote?.contractor_id && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.contractorDetails}</h2>
            <div className="work-details-card">
              <div className="client-info-item">
                <h4 className="work-subsection-title">{t.company}</h4>
                <p className="client-info-text">
                  <span
                    className="contractor-name-link"
                    onClick={() => navigate(`/contractor/${work.approved_quote.contractor_id._id}`)}
                  >
                    {work.approved_quote.contractor_id.name || "N/A"}
                  </span>
                  {work.approved_quote.contractor_id.average_rating !== undefined && (
                    <span className="contractor-rating-link">
                      {" "}{t.clickToKnowMore}
                    </span>
                  )}
                </p>
              </div>

              {work.approved_quote.contractor_id.phone && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.phone}</h4>
                  <a 
                    href={`tel:${work.approved_quote.contractor_id.phone}`}
                    className="client-info-link"
                  >
                    {work.approved_quote.contractor_id.phone}
                  </a>
                </div>
              )}

              {work.approved_quote.contractor_id.whatsapp_number && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.whatsapp}</h4>
                  <a 
                    href={`https://wa.me/${work.approved_quote.contractor_id.whatsapp_number.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="client-info-link"
                  >
                    {work.approved_quote.contractor_id.whatsapp_number}
                  </a>
                </div>
              )}

              {work.approved_quote.contractor_id.email && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.email}</h4>
                  <a 
                    href={`mailto:${work.approved_quote.contractor_id.email}`}
                    className="client-info-link"
                  >
                    {work.approved_quote.contractor_id.email}
                  </a>
                </div>
              )}

              {work.approved_quote.contractor_id.average_rating !== undefined && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.ratings}</h4>
                  {renderStars(work.approved_quote.contractor_id.average_rating || 0)}
                  <p className="rating-hint">
                    {(work.approved_quote.contractor_id.average_rating || 0).toFixed(1)} / 5.0
                  </p>
                </div>
              )}
              {Array.isArray(work.approved_quote.contractor_id.specialisations) &&
                work.approved_quote.contractor_id.specialisations.length > 0 && (
                  <div className="client-info-item">
                    <h4 className="work-subsection-title">{t.specialisations}</h4>
                    <div className="work-subcategories-list">
                      {work.approved_quote.contractor_id.specialisations.map((spec, index) => (
                        <span key={spec._id || index} className="subcategory-chip">
                          {spec.name}
                        </span>
                      ))}
                    </div>
                  </div>
              )}
            </div>
          </div>
        )}

        {/* Quote Details */}
        {quotesToDisplay.length > 0 && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.quoteDetails}</h2>
            <div className="quotes-cards-container">
              {quotesToDisplay.map((quote, index) => (
                <div key={quote._id || index} className="work-details-card">
                  <div className="quote-timeline-item">
                    <div className="quote-timeline-header">
                      <span className="quote-date">{formatDateTime(quote.created_at)}</span>
                      <span
                        className={`quote-status-badge ${quote.status?.toLowerCase()}`}
                        style={{
                          backgroundColor:
                            quote.status === "pending"
                              ? "#47c0c9"
                              : quote.status === "approved"
                              ? "#0AC26F"
                              : "#FF5252",
                          color: "#fff",
                        }}
                      >
                        {quote.status === "pending"
                          ? t.pending
                          : quote.status === "approved"
                          ? t.accepted
                          : t.rejected}
                      </span>
                    </div>
                    <div className="quote-timeline-content">
                      {quote.quote !== undefined && (
                        <div className="quote-info-row">
                          <div className="quote-info-label">{t.quoteAmount}</div>
                          <p className="quote-info-value">AED {formatAmount(quote.quote)}</p>
                        </div>
                      )}

                      {quote.documents && quote.documents.length > 0 && (
                        <div className="quote-info-row">
                          <div className="quote-info-label">{t.documents}</div>
                          <div className="quote-documents-list">
                            {quote.documents.map((doc, docIndex) => {
                              const isLoading = loadingDocumentId === doc.id;
                              return (
                                <div
                                  key={doc.id || docIndex}
                                  className={`quote-document-item ${isLoading ? "loading" : ""}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (doc.id && !isLoading && work?._id) {
                                      handleViewQuoteDocument(doc.id, quote._id);
                                    } else if (!doc.id) {
                                      showToast("Document ID not available", "error");
                                    }
                                  }}
                                >
                                  {isLoading ? (
                                    <div className="small-document-loading-spinner">
                                      <div className="spinner-small"></div>
                                    </div>
                                  ) : (
                                    <div className="small-document-icon">PDF</div>
                                  )}
                                  <span className="small-document-name">
                                    {doc.id?.split("/").pop()?.substring(0, 15) || "Document"}
                                    ...
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {quote.contractor_id && quote.status !== "approved" && (
                        <div className="quote-info-row">
                          <div className="quote-info-label">{t.company}</div>
                          <p className="quote-info-value">
                            <span
                              className="contractor-name-link"
                              onClick={() => navigate(`/contractor/${quote.contractor_id._id}`)}
                            >
                              {quote.contractor_id.name || "N/A"}
                            </span>
                            {quote.contractor_id.average_rating !== undefined && (
                              <span className="contractor-rating-link">
                                {" "}{t.clickToKnowMore}
                              </span>
                            )}
                          </p>
                          {quote.contractor_id.average_rating !== undefined && (
                            <div>
                              {renderStars(quote.contractor_id.average_rating || 0)}
                              <p className="rating-hint">
                                {(quote.contractor_id.average_rating || 0).toFixed(1)} / 5.0
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Show rejection note if rejected */}
                      {quote.status === "rejected" && quote.rejection_note && (
                        <div className="quote-rejection-note">
                          <strong>{t.rejectionNote}:</strong> {quote.rejection_note}
                        </div>
                      )}

                      {/* Approve/Reject buttons - only show if status is pending and work is not completed */}
                      {quote.status === "pending" && !isCompleted && (
                        <div className="quote-actions">
                          <button
                            className="quote-action-btn reject"
                            onClick={() => handleRejectQuoteClick(quote)}
                            disabled={processingQuoteAction}
                            style={{ color: "#ef4444" }}
                          >
                            {t.rejectQuote}
                          </button>
                          <button
                            className="quote-action-btn approve"
                            onClick={() => handleApproveQuoteClick(quote)}
                            disabled={processingQuoteAction}
                            style={{ color: "#47c0c9" }}
                          >
                            {t.approveQuote}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!isCompleted && (
          <button
            className="work-action-button"
            onClick={handleMarkAsCompleted}
            disabled={markingCompleted}
          >
            {t.markWorkCompleted}
          </button>
        )}

        {isCompleted && hasApprovedQuote && (
          <button
            className="work-action-button"
            onClick={handleReviewContractorClick}
          >
            {t.reviewContractor}
          </button>
        )}
      </div>

      {/* Approve Quote Modal */}
      {showApproveQuoteModal && selectedQuote && (
        <div
          className="quote-modal-overlay"
          onClick={() => !processingQuoteAction && setShowApproveQuoteModal(false)}
        >
          <div
            className="quote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quote-modal-header">
              <h2 className="quote-modal-title">{t.approveQuote}</h2>
              <button
                className="quote-modal-close"
                onClick={() => !processingQuoteAction && setShowApproveQuoteModal(false)}
                disabled={processingQuoteAction}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="quote-modal-body">
              {selectedQuote.quote !== undefined && (
                <div className="quote-modal-info">
                  <h4>{t.quoteAmount}</h4>
                  <p className="quote-amount-large">AED {formatAmount(selectedQuote.quote)}</p>
                </div>
              )}

              {selectedQuote.contractor_id && (
                <div className="quote-modal-info">
                  <h4>{t.company}</h4>
                  <p>
                    <span
                      className="contractor-name-link"
                      onClick={() => navigate(`/contractor/${selectedQuote.contractor_id._id}`)}
                    >
                      {selectedQuote.contractor_id.name || "N/A"}
                    </span>
                  </p>
                </div>
              )}

              <div className="quote-modal-info">
                <h4>{t.importantInfo}</h4>
                <p className="declaration-text">{t.importantInfoText}</p>
              </div>

              <div className="quote-modal-info">
                <h4>{t.declaration}</h4>
                <p className="declaration-text">{t.approveDeclarationText}</p>
              </div>

              <label className="declaration-checkbox-label">
                <input
                  type="checkbox"
                  checked={approveDeclarationChecked}
                  onChange={(e) => setApproveDeclarationChecked(e.target.checked)}
                  disabled={processingQuoteAction}
                />
                <span className="declaration-checkbox-custom"></span>
                <span className="declaration-checkbox-text">{t.agreeDeclaration}</span>
              </label>
            </div>

            <div className="quote-modal-footer">
              <button
                className="quote-modal-btn approve"
                onClick={handleApproveQuote}
                disabled={processingQuoteAction || !approveDeclarationChecked}
              >
                {processingQuoteAction ? t.loadingApprove : t.approveQuote}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Quote Modal */}
      {showRejectQuoteModal && selectedQuote && (
        <div
          className="quote-modal-overlay"
          onClick={() => !processingQuoteAction && setShowRejectQuoteModal(false)}
        >
          <div
            className="quote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quote-modal-header">
              <h2 className="quote-modal-title">{t.rejectQuote}</h2>
              <button
                className="quote-modal-close"
                onClick={() => !processingQuoteAction && setShowRejectQuoteModal(false)}
                disabled={processingQuoteAction}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="quote-modal-body">
              {selectedQuote.quote !== undefined && (
                <div className="quote-modal-info">
                  <h4>{t.quoteAmount}</h4>
                  <p className="quote-amount-large">
                    AED {formatAmount(selectedQuote.quote)}
                  </p>
                </div>
              )}

              {selectedQuote.contractor_id && (
                <div className="quote-modal-info">
                  <h4>{t.company}</h4>
                  <p>
                    <span
                      className="contractor-name-link"
                      onClick={() => navigate(`/contractor/${selectedQuote.contractor_id._id}`)}
                    >
                      {selectedQuote.contractor_id.name || "N/A"}
                    </span>
                  </p>
                </div>
              )}

              <div className="quote-modal-info">
                <h4>{t.rejectionNote}</h4>
                <textarea
                  className="rejection-note-input"
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  placeholder={t.rejectionNotePlaceholder}
                  rows="4"
                  maxLength="500"
                  disabled={processingQuoteAction}
                />
              </div>

              <div className="quote-modal-info">
                <h4>{t.declaration}</h4>
                <p className="declaration-text">{t.rejectDeclarationText}</p>
              </div>

              <label className="declaration-checkbox-label">
                <input
                  type="checkbox"
                  checked={rejectDeclarationChecked}
                  onChange={(e) => setRejectDeclarationChecked(e.target.checked)}
                  disabled={processingQuoteAction}
                />
                <span className="declaration-checkbox-custom"></span>
                <span className="declaration-checkbox-text">{t.agreeDeclaration}</span>
              </label>
            </div>

            <div className="quote-modal-footer">
              <button
                className="quote-modal-btn reject"
                onClick={handleRejectQuote}
                disabled={processingQuoteAction || !rejectDeclarationChecked || !rejectionNote.trim()}
              >
                {processingQuoteAction ? t.loadingReject : t.rejectQuote}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Contractor Modal */}
      {showReviewModal && contractorForReview && (
        <div
          className="review-modal-overlay"
          onClick={() => !submittingReview && !loadingReview && setShowReviewModal(false)}
        >
          <div
            className="review-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="review-modal-header">
              <h2 className="review-modal-title">
                {reviewStars > 0 || reviewText ? t.editReview : t.addReview}
              </h2>
              <button
                className="review-modal-close"
                onClick={() => !submittingReview && !loadingReview && setShowReviewModal(false)}
                disabled={submittingReview || loadingReview}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="review-modal-body">
              {loadingReview ? (
                <div className="review-loading">
                  <div className="spinner"></div>
                  <p>{t.loading}</p>
                </div>
              ) : (
                <>
                  <div className="review-modal-info">
                    <h4>{t.company}</h4>
                    <p>
                      <span
                        className="contractor-name-link"
                        onClick={() => navigate(`/contractor/${contractorForReview._id}`)}
                      >
                        {contractorForReview.name || "N/A"}
                      </span>
                    </p>
                  </div>

                  <div className="review-form-group">
                    <label className="review-form-label">{t.yourRating}</label>
                    <div className="review-stars-input">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          className={`review-star-btn ${
                            star <= reviewStars ? "active" : ""
                          }`}
                          onClick={() => setReviewStars(star)}
                          disabled={submittingReview}
                        >
                          <i className="bx bxs-star"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="review-form-group">
                    <label className="review-form-label">{t.yourReview}</label>
                    <textarea
                      className="review-form-textarea"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder={t.reviewPlaceholder}
                      rows="5"
                      maxLength="1000"
                      disabled={submittingReview}
                    />
                    <p className="review-char-count">
                      {reviewText.length}/1000
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="review-modal-footer">
              <button
                className="review-modal-btn cancel"
                onClick={() => !submittingReview && !loadingReview && setShowReviewModal(false)}
                disabled={submittingReview || loadingReview}
              >
                {t.close}
              </button>
              <button
                className="review-modal-btn submit"
                onClick={handleSubmitReview}
                disabled={submittingReview || loadingReview || !reviewStars || !reviewText.trim()}
              >
                {submittingReview ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Work Complete Confirmation Modal */}
      {showCompleteConfirmModal && (
        <div
          className="delete-confirm-modal-overlay"
          onClick={cancelMarkAsCompleted}
        >
          <div
            className="delete-confirm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-confirm-modal-header">
              <h3 className="delete-confirm-modal-title">{t.confirmComplete}</h3>
            </div>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-modal-message">
                {t.completeWorkConfirm}
              </p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button
                className="delete-confirm-modal-btn cancel"
                onClick={cancelMarkAsCompleted}
                disabled={markingCompleted}
              >
                {t.cancel}
              </button>
              <button
                className="delete-confirm-modal-btn confirm"
                onClick={confirmMarkAsCompleted}
                disabled={markingCompleted}
                style={markingCompleted ? { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" } : {}}
              >
                {markingCompleted ? (
                  <>
                    <div className="button-spinner"></div>
                    {t.loadingComplete}
                  </>
                ) : (
                  t.confirmComplete
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavbar currentPage="home" lang={lang} />
    </div>
  );
}
